import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageToggle from './LanguageToggle';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { getCategories, Category } from '../api/products';

const Header: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { getSetting } = useSettings();
    const { itemCount, setIsCartOpen } = useCart();
    const [isScrolled, setIsScrolled] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Scroll handler and Category fetching
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);

        // Fetch categories
        getCategories().then(data => {
            const sorted = [...data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setCategories(sorted);
        }).catch(error => {
            console.error('Error fetching categories:', error);
        });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    // Prevent scrolling when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    const baseLinks = [
        { path: '/', label: t('common.home') },
        { path: '/category/all', label: t('common.products') },
    ];

    const categoryLinks = categories.map(category => ({
        path: `/category/${category.slug}`,
        label: i18n.language === 'ar' ? category.name_ar : category.name_fr
    }));

    const infoLinks = [
        { path: '/about', label: t('common.about') },
        { path: '/contact', label: t('common.contact') },
    ];

    // Primary items: Home, Products, and first 3 categories
    const primaryNavLinks = [...baseLinks, ...categoryLinks.slice(0, 3)];
    // Secondary items (dropdown): Remaining categories + Info links
    const secondaryNavLinks = [...categoryLinks.slice(3), ...infoLinks];

    // Combined for mobile menu
    const mobileNavLinks = [...baseLinks, ...categoryLinks, ...infoLinks];

    const [isMoreOpen, setIsMoreOpen] = useState(false);

    return (
        <motion.header
            className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled
                ? 'bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20'
                : 'bg-white border-b border-transparent'
                }`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                {/* Mobile Menu Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="md:hidden p-2 text-neutral-600 hover:text-primary transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>

                <Link to="/" className="relative block">
                    <img src="/logo.png" alt={getSetting('site_name', 'Farhan')} className="h-10 w-auto" />
                </Link>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
                    {primaryNavLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`relative text-sm font-medium transition-colors hover:text-primary ${location.pathname === link.path ? 'text-primary' : 'text-neutral-600'
                                }`}
                        >
                            {link.label}
                            {location.pathname === link.path && (
                                <motion.span
                                    layoutId="underline"
                                    className="absolute left-0 top-full block h-0.5 w-full bg-primary mt-1"
                                />
                            )}
                        </Link>
                    ))}

                    {/* Desktop Dropdown for "More" */}
                    {secondaryNavLinks.length > 0 && (
                        <div
                            className="relative group"
                            onMouseEnter={() => setIsMoreOpen(true)}
                            onMouseLeave={() => setIsMoreOpen(false)}
                        >
                            <button className="flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-primary transition-colors py-2">
                                <span>{t('common.more')}</span>
                                <svg
                                    className={`w-4 h-4 transition-transform duration-200 ${isMoreOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {isMoreOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute top-full ltr:left-0 rtl:right-0 mt-1 w-56 bg-white rounded-2xl shadow-xl border border-neutral-100 py-3 z-50 overflow-hidden"
                                    >
                                        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                                            {secondaryNavLinks.map((link) => (
                                                <Link
                                                    key={link.path}
                                                    to={link.path}
                                                    className={`block px-5 py-2.5 text-sm transition-colors hover:bg-neutral-50 hover:text-primary ${location.pathname === link.path ? 'bg-primary/5 text-primary font-bold' : 'text-neutral-600'
                                                        }`}
                                                >
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </nav>

                {/* Actions */}
                <div className="flex items-center space-x-4 rtl:space-x-reverse">


                    <LanguageToggle />

                    <button
                        onClick={() => setIsCartOpen(true)}
                        className="relative p-2 text-neutral-600 hover:text-primary transition-colors group"
                    >
                        <div className="absolute inset-0 bg-primary/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300"></div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        <AnimatePresence>
                            {itemCount > 0 && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold leading-none text-white bg-accent rounded-full shadow-lg ring-2 ring-white"
                                >
                                    {itemCount}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="fixed inset-0 bg-black/50 z-50 md:hidden backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ x: i18n.dir() === 'rtl' ? '100%' : '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: i18n.dir() === 'rtl' ? '100%' : '-100%' }}
                            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                            className="fixed inset-y-0 ltr:left-0 rtl:right-0 w-[80%] max-w-sm h-screen bg-white z-[51] md:hidden shadow-2xl overflow-y-auto"
                        >
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-8">
                                    <img src="/logo.png" alt={getSetting('site_name', 'Farhan')} className="h-8 w-auto" />
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <nav className="flex flex-col space-y-4">
                                    {mobileNavLinks.map((link, index) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={`flex items-center justify-between p-3 rounded-xl transition-all ${location.pathname === link.path
                                                ? 'bg-primary/10 text-primary font-bold'
                                                : 'text-gray-600 hover:bg-gray-50'
                                                }`}
                                        >
                                            <span>{link.label}</span>
                                            {location.pathname === link.path && (
                                                <div className="w-2 h-2 rounded-full bg-primary" />
                                            )}
                                        </Link>
                                    ))}
                                </nav>

                                <div className="mt-8 pt-8 border-t border-gray-100">
                                    <p className="text-sm text-gray-400 mb-4 text-center">{t('common.language')}</p>
                                    <div className="flex justify-center">
                                        <LanguageToggle />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
